import React, { useCallback, useRef, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import './styles/index.scss';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Wrap,
  WrapItem,
  HStack,
  IconButton,
  Divider,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { pxToRem, colorTheme, useHandleRubricAnswerChange, spanSelectors } from '@codametrix/ui-common';
import { Lookup, shouldShowResults, MedicalCodeChip, Icon, FieldDefinitions } from '@codametrix/ui-components';
import HighlightDrawerCodeContainer from './drawer-code-container/highlight-drawer-code-container';
import { ResultType, SuggestedLookup } from '../code-bench-panel/lookup/lookupV2';
import { getWorkflowForCodeType, findAnswerForQuestion, getQuestionIdsToClear, findSpanScopedWorkflowMatchesForSpan } from './rubric-utils';
import { RubricQuestionField } from './rubric-question-field';
import { SpanScopedWorkflowSection } from './span-scoped-workflow-section';
import {
  useWorkflowEvaluation,
  EvaluateWorkflowFn,
} from './useWorkflowEvaluation';
import { useRubricAutofill } from './useRubricAutofill';

const CODE_TYPE_MAP: Record<string, CMxCommonApp.ChargeSessionCodeType> = {
  'CPT': 'procedure',
  'ICD': 'diagnosis',
  'MODIFIER': 'modifier',
  'procedure': 'procedure',
  'diagnosis': 'diagnosis',
  'modifier': 'modifier'
};

const noopAttributeChange = (_attribute: CQA.UpdateAttributeParams) => {};
const noopDeleteAttribute = async (_params: CQA.DeleteAttributeParams) => {};

/**
 * Props that drive the assessment and role selections for codes in the highlight drawer
 */
export type CodeAssessmentProps = {
  cptFields?: CMxCommonApp.CodeLayerField[];
  icdFields?: CMxCommonApp.CodeLayerField[];
  modifierFields?: CMxCommonApp.CodeLayerField[];
  cqaCodeAssessmentRiskDictionaries?: Accelerate.CQACodeAssessmentRiskDictionaries;
  projectConfiguration?: CMxAPI.CQAProjectConfiguration;
  onCodeAssessmentUpdate?: (code: CQA.ExtractedCode, field: CMxCommonApp.SelectListField, fields: CMxCommonApp.CodeLayerField[], value: string) => void | Promise<void>;
};

export interface HighlightDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "full";
  attachedCodes?: CQA.AttachedCodes;
  spanScopeSourceCodes?: CQA.AttachedCodes;
  availableCodes?: CQA.ExtractedCode[];
  codeSearch?: CQA.CodeSearchProps;
  selectedSpan?: Accelerate.SpanAnnotation;
  caseId?: string;
  onAttachCode?: (code: CQA.ExtractedCode, annotation: Partial<Accelerate.SpanAnnotation>) => Promise<void>;
  onDetachCode?: (code: CQA.ExtractedCode, annotation: Partial<Accelerate.SpanAnnotation>) => Promise<void>;
  onDelete?: () => Promise<void>;
  riskCategories?: Accelerate.RiskCategoryTree;
  // These are the props that drive the assessment and role selections
  codeAssessmentProps?: CodeAssessmentProps;
  suggestedCodes?: CMxCommonApp.SuggestedCodes;
  // Rubric version for dynamic code questions
  rubricVersion?: CQA.FullRubricVersion | null;
  onAttributeChange?: (attribute: CQA.UpdateAttributeParams) => void;
  onDeleteAttribute: (params: CQA.DeleteAttributeParams) => Promise<void>;
  editable?: boolean;
  onClearDownstream?: (params: CQA.ClearDownstreamAttributesParams) => void;
  // Backend-driven workflow evaluation
  evaluateFn?: EvaluateWorkflowFn;
  workItemId?: number;
}

export const HighlightDrawer: React.FC<HighlightDrawerProps> = ({
  isOpen,
  onClose,
  size = "sm",
  attachedCodes,
  spanScopeSourceCodes,
  availableCodes,
  codeSearch,
  selectedSpan,
  caseId,
  onAttachCode,
  onDetachCode,
  onDelete,
  riskCategories,
  codeAssessmentProps,
  suggestedCodes,
  rubricVersion,
  onAttributeChange,
  onDeleteAttribute,
  editable = true,
  onClearDownstream,
  evaluateFn,
  workItemId
}) => {
  const {
    cptFields,
    icdFields,
    modifierFields,
    cqaCodeAssessmentRiskDictionaries,
    projectConfiguration,
    onCodeAssessmentUpdate
  } = codeAssessmentProps || {};

  const procedureAllowNewCodes = projectConfiguration?.procedure ?? projectConfiguration?.tasks?.procedure?.allow_new_codes;
  const diagnosisAllowNewCodes = projectConfiguration?.diagnosis ?? projectConfiguration?.tasks?.diagnosis?.allow_new_codes;
  const modifierAllowNewCodes = projectConfiguration?.modifier ?? projectConfiguration?.tasks?.modifier?.allow_new_codes;

  const procedureVisible = projectConfiguration?.procedure ?? projectConfiguration?.tasks?.procedure?.visible;
  const diagnosisVisible = projectConfiguration?.diagnosis ?? projectConfiguration?.tasks?.diagnosis?.visible;
  const modifierVisible = projectConfiguration?.modifier ?? projectConfiguration?.tasks?.modifier?.visible;

  // Respect project code type collection: only show sections for enabled types (default true when config missing)
  const showProcedure = procedureVisible !== false;
  const showModifier = modifierVisible !== false;
  const showDiagnosis = diagnosisVisible !== false;

  // Filter available codes to only those for enabled code types
  const filteredAvailableCodes = useMemo(() => {
    if (!availableCodes?.length) return availableCodes ?? [];
    return availableCodes.filter((code) => {
      const type = code.codeType as CMxCommonApp.ChargeSessionCodeType;
      if (type === 'procedure') return showProcedure;
      if (type === 'modifier') return showModifier;
      if (type === 'diagnosis') return showDiagnosis;
      return true;
    });
  }, [availableCodes, showProcedure, showModifier, showDiagnosis]);

  const lookupRef = useRef(null);
  const modifierLookupRef = useRef(null);
  const diagnosisLookupRef = useRef(null);
  const hasRubricVersion = Boolean(
    rubricVersion || projectConfiguration?.rubricVersionId
  );

  // Get workflows for each code type
  const procedureWorkflow = useMemo(() =>
    getWorkflowForCodeType(rubricVersion, 'procedure'),
    [rubricVersion]
  );

  const modifierWorkflow = useMemo(() =>
    getWorkflowForCodeType(rubricVersion, 'modifier'),
    [rubricVersion]
  );

  const diagnosisWorkflow = useMemo(() =>
    getWorkflowForCodeType(rubricVersion, 'diagnosis'),
    [rubricVersion]
  );

  const spanWorkflow = useMemo(() =>
    getWorkflowForCodeType(rubricVersion, 'span'),
    [rubricVersion]
  );

  const procedureExistingCodes = useMemo(
    () => attachedCodes?.procedures?.map(p => ({ code: p.value, codeId: p.codeId })) as unknown as CMxCommonApp.ChargeSessionCode[],
    [attachedCodes?.procedures]
  );
  const modifierExistingCodes = useMemo(
    () => attachedCodes?.modifiers?.map(p => ({ code: p.value, codeId: p.codeId })) as unknown as CMxCommonApp.ChargeSessionCode[],
    [attachedCodes?.modifiers]
  );
  const diagnosisExistingCodes = useMemo(
    () => attachedCodes?.diagnoses?.map(p => ({ code: p.value, codeId: p.codeId })) as unknown as CMxCommonApp.ChargeSessionCode[],
    [attachedCodes?.diagnoses]
  );

  const allSpans = useSelector(
    (state: any) => spanSelectors.selectSpanAnnotations(state.gradeBenchPanel)
  ) as Accelerate.SpanAnnotation[];

  // Gate span and code questions behind case-level completion
  const caseLevelComplete = useSelector(
    (state: any) => state.gradeBenchPanel?.caseLevelComplete
  );
  const spanQuestionsDisabled = caseLevelComplete === false;

  const legacySpanAttributes = useMemo(
    () => (selectedSpan?.rubricAttributes || []).filter((a) => (a.spanScopeAttributeId ?? a.parentQuestionId) == null),
    [selectedSpan?.rubricAttributes],
  );

  const allSpanWorkflowQuestions = useMemo(
    () => (spanWorkflow?.questions || []).filter(
      (wq) => wq.spanScope !== 'SELECTED_BY_SPANS_QUESTION',
    ),
    [spanWorkflow?.questions],
  );

  // Use backend-driven workflow evaluation for span questions
  const {
    evaluation: spanEvaluation,
    visibleQuestions: visibleSpanQuestions,
    filteredAnswersMap: spanFilteredAnswersMap,
    autofilledAnswersMap: spanAutofilledAnswersMap,
    isLoading: isSpanLoading,
    error: spanError,
  } = useWorkflowEvaluation({
    workflowId: spanWorkflow?.id,
    attributes: legacySpanAttributes,
    workflowQuestions: allSpanWorkflowQuestions,
    workItemId,
    evaluateFn: evaluateFn!,
    enabled: !!evaluateFn && !!spanWorkflow,
  });

  const [lastChangedSpanQuestionId, setLastChangedSpanQuestionId] = useState<number | null>(null);

  const caseAttributes = useSelector(
    (state: any) => state.gradeBenchPanel?.caseAttributes,
  ) as CQA.Attribute[] | undefined;

  const spanScopedWorkflowMatches = useMemo(
    () =>
      findSpanScopedWorkflowMatchesForSpan(
        rubricVersion ?? null,
        selectedSpan?.id,
        spanScopeSourceCodes ?? attachedCodes,
        caseAttributes,
      ),
    [rubricVersion, selectedSpan?.id, spanScopeSourceCodes, attachedCodes, caseAttributes],
  );

  const rubricVersionId = rubricVersion?.version?.id ?? projectConfiguration?.rubricVersionId;

  // Handle attaching a code
  const handleAttachCode = useCallback(async (code: CQA.ExtractedCode) => {
    if (!onAttachCode || !selectedSpan || !editable) return;

    // Build updated annotation with the new code
    const updatedAnnotation: Partial<Accelerate.SpanAnnotation> = {
      id: selectedSpan.id,
      beginOffset: selectedSpan.beginOffset,
      endOffset: selectedSpan.endOffset,
      coveredText: selectedSpan.coveredText,
      attributes: [
        ...(selectedSpan.attributes || [])
      ]
    };

    // Make API call
    await onAttachCode(code, updatedAnnotation);
  }, [onAttachCode, selectedSpan, editable]);

  // Get search results for this highlight drawer context
  // Use selectedSpan id as the lookup key
  const searchResultsKey = selectedSpan?.id || 'highlight-drawer';
  const allSearchResults = codeSearch?.searchResults?.[searchResultsKey] ?? [];

  // Filter search results by code type
  const procedureSearchResults = allSearchResults.filter(
    (result) => result.codeType === 'CPT' || result.codeType === 'procedure'
  );
  const modifierSearchResults = allSearchResults.filter(
    (result) => result.codeType === 'MODIFIER' || result.codeType === 'modifier'
  );
  const diagnosisSearchResults = allSearchResults.filter(
    (result) => result.codeType === 'ICD' || result.codeType === 'diagnosis'
  );

  // Handle code search
  const onSearch = useCallback((codeQuery: CMxCommonApp.CodeQuery) => {
    if (!codeSearch?.onCodeSearch || !editable) return;

    codeSearch.onCodeSearch({
      ...codeQuery,
      context: {
        ...codeQuery.context,
        selectedSpan: selectedSpan ? { ...selectedSpan } : undefined
      }
    });
  }, [codeSearch, selectedSpan, editable]);

  // Handle code selection from search
  const onCodeSelect = useCallback((codeAction: Accelerate.CodeActionPayload) => {
    if (!codeSearch?.onCodeSearchSelection || !selectedSpan || !editable) return;

    // Call the code search selection handler - this will handle the actual attachment
    // with proper context including documentId, caseId, and spans
    const payload: Accelerate.CodeAddActionPayload = {
      codeInfo: codeAction.codeInfo,
      context: codeAction.context,
      documentId: selectedSpan.documentId,
      caseId: caseId,
      spans: selectedSpan ? [selectedSpan] : undefined
    };

    codeSearch.onCodeSearchSelection(payload);
  }, [codeSearch, selectedSpan, caseId, editable]);

  const handleCodeSelection = (selection: {
    codeInfo: CMxCommonApp.CodeInfo;
    additionalCodes?: CMxCommonApp.CodeInfo[][];
    context: { source?: ResultType; [key: string]: any };
  }) => {
    if (!editable) return;

    const { context, codeInfo } = selection;
    const codeType = codeInfo.codeType;
    if (context?.source === ResultType.SUGGESTED_RESULTS) {
      // Attach to existing code
      handleAttachCode({
        ...codeInfo, spanId: selectedSpan.id,
        codeId: codeInfo?.codeId,
        codeType: CODE_TYPE_MAP[codeInfo?.codeType],
        compositeId: '',
        value: '',
        invoiceId: 0,
        transactionId: 0
      })
    } else {
      // Add new code/charge
      onCodeSelect(selection);
    }
  };


  // Handle detaching a code from the span
  const handleDetachCode = useCallback(async (code: CQA.ExtractedCode) => {
    if (!onDetachCode || !selectedSpan || !editable) return;

    // Build updated annotation without the detached code
    const updatedAnnotation: Partial<Accelerate.SpanAnnotation> = {
      id: selectedSpan.id,
      beginOffset: selectedSpan.beginOffset,
      endOffset: selectedSpan.endOffset,
      coveredText: selectedSpan.coveredText,
      attributes: [
        ...(selectedSpan.attributes || [])
      ]
    };

    // Make API call to detach the code
    await onDetachCode(code, updatedAnnotation);
  }, [onDetachCode, selectedSpan, editable]);

  // Handle deleting the span
  const handleDeleteSpan = useCallback(async () => {
    if (onDelete && editable) {
      await onDelete();
      onClose();
    }
  }, [onDelete, onClose, editable]);

  // Handle assessment change
  const handleAssessmentChange = useCallback((code: CQA.ExtractedCode, field: CMxCommonApp.SelectListField, fields: CMxCommonApp.CodeLayerField[], value: string) => {
    if (onCodeAssessmentUpdate && editable) {
      onCodeAssessmentUpdate(code, field, fields, value);
    }
  }, [onCodeAssessmentUpdate, editable]);

  // Handle role change
  const handleRoleChange = useCallback((code: CQA.ExtractedCode, field: CMxCommonApp.SelectListField, fields: CMxCommonApp.CodeLayerField[], value: string) => {
    if (onCodeAssessmentUpdate && editable) {
      onCodeAssessmentUpdate(code, field, fields, value);
    }
  }, [onCodeAssessmentUpdate, editable]);

  const handleSpanAnswerChangeWithCleanup = useHandleRubricAnswerChange(
    onAttributeChange || noopAttributeChange,
    onDeleteAttribute,
    onClearDownstream
  );

  // Use a ref to break the circular dependency between hook and handler
  const handleSpanAttributeChangeRef = useRef<(questionId: number, value: number | number[] | string) => void>(() => {});

  // Use the autofill hook for span questions
  const { trackAnswerChange: trackSpanAnswerChange, clearDownstreamTracking: clearSpanDownstreamTracking } = useRubricAutofill({
    entityId: String(selectedSpan?.id || ''),
    entityKey: `span-${selectedSpan?.id}`,
    attributes: legacySpanAttributes,
    autofilledAnswers: spanAutofilledAnswersMap,
    onAutofill: (questionId, answerValueId) => {
      handleSpanAttributeChangeRef.current(questionId, answerValueId);
    }
  });

  // Handle span attribute changes (value: number = answerValueId for categorical, number[] = spanAnnotationIds for SPAN/SPANS, string = answerDisplayText for FREE_TEXT)
  const handleSpanAttributeChange = useCallback(async (questionId: number, value: number | number[] | string) => {
    if (spanQuestionsDisabled) return;
    if (!onAttributeChange || !selectedSpan) return;

    setLastChangedSpanQuestionId(questionId);

    const spanAttributes = legacySpanAttributes;
    const downstreamQuestionIds = getQuestionIdsToClear(
      visibleSpanQuestions,
      spanAttributes,
      questionId
    );

    const isFreeText = typeof value === 'string';
    const isArray = Array.isArray(value);
    if (!isFreeText && !isArray) {
      trackSpanAnswerChange(questionId, value);
    }
    clearSpanDownstreamTracking(downstreamQuestionIds);

    // Determine which answer field to use based on question type
    const question = visibleSpanQuestions?.find(wq => wq.question.id === questionId)?.question;
    const isSpan = question?.questionType === 'SPAN';
    const isSpans = question?.questionType === 'SPANS';

    let answerFields: Record<string, unknown>;
    if (isFreeText) {
      answerFields = { answerDisplayText: value };
    } else if (isSpan || isSpans) {
      answerFields = { spanAnnotationIds: value };
    } else {
      answerFields = { answerValueId: value };
    }

    await handleSpanAnswerChangeWithCleanup({
      entityId: String(selectedSpan.id),
      entityType: 'span' as CQA.AttributeEntityType,
      attributes: spanAttributes,
      questionId,
      ...answerFields,
      downstreamQuestionIds
    });
  }, [spanQuestionsDisabled, onAttributeChange, selectedSpan, legacySpanAttributes, visibleSpanQuestions, handleSpanAnswerChangeWithCleanup, trackSpanAnswerChange, clearSpanDownstreamTracking]);

  handleSpanAttributeChangeRef.current = handleSpanAttributeChange;

  const createRemoveCodesHandler = useCallback((codeType: 'procedure' | 'modifier' | 'diagnosis') => {
  return async (codes: CMxCommonApp.SuggestedCode[]) => {
    if (!editable) return;

    const codeListMap = {
      procedure: attachedCodes?.procedures,
      modifier: attachedCodes?.modifiers,
      diagnosis: attachedCodes?.diagnoses
    };
    
    const codeList = codeListMap[codeType];
    
    for (const code of codes) {
      const extractedCode = codeList?.find(c => c.codeId === code.codeId);
      if (extractedCode) await handleDetachCode(extractedCode);
    }
  };
}, [handleDetachCode, attachedCodes, editable]);

  const procedureCodeContainers = attachedCodes?.procedures?.map((procedure) => (
    <HighlightDrawerCodeContainer
      key={procedure.compositeId}
      code={procedure}
      onDelete={handleDetachCode}
      onAssessmentChange={handleAssessmentChange}
      onRoleChange={handleRoleChange}
      riskCategories={riskCategories}
      cqaCodeAssessmentRiskDictionaries={cqaCodeAssessmentRiskDictionaries}
      projectConfiguration={projectConfiguration}
      cptFields={cptFields}
      icdFields={icdFields}
      modifierFields={modifierFields}
      rubricWorkflow={procedureWorkflow}
      hasRubricVersion={hasRubricVersion}
      onAnswerChange={onAttributeChange}
      onDeleteAnswer={onDeleteAttribute}
      onClearDownstream={onClearDownstream}
      evaluateFn={evaluateFn}
      workItemId={workItemId}
      spans={allSpans}
      editable={editable}
    />
  ));

  const modifierCodeContainers = attachedCodes?.modifiers?.map((modifier) => (
    <HighlightDrawerCodeContainer
      key={modifier.compositeId}
      code={modifier}
      onDelete={handleDetachCode}
      onAssessmentChange={handleAssessmentChange}
      onRoleChange={handleRoleChange}
      riskCategories={riskCategories}
      cqaCodeAssessmentRiskDictionaries={cqaCodeAssessmentRiskDictionaries}
      projectConfiguration={projectConfiguration}
      cptFields={cptFields}
      icdFields={icdFields}
      modifierFields={modifierFields}
      rubricWorkflow={modifierWorkflow}
      hasRubricVersion={hasRubricVersion}
      onAnswerChange={onAttributeChange}
      onDeleteAnswer={onDeleteAttribute}
      onClearDownstream={onClearDownstream}
      evaluateFn={evaluateFn}
      workItemId={workItemId}
      spans={allSpans}
      editable={editable}
    />
  ));

  const diagnosisCodeContainers = attachedCodes?.diagnoses?.map((diagnosis) => (
    <HighlightDrawerCodeContainer
      key={diagnosis.compositeId}
      code={diagnosis}
      onDelete={handleDetachCode}
      onAssessmentChange={handleAssessmentChange}
      onRoleChange={handleRoleChange}
      riskCategories={riskCategories}
      cqaCodeAssessmentRiskDictionaries={cqaCodeAssessmentRiskDictionaries}
      projectConfiguration={projectConfiguration}
      cptFields={cptFields}
      icdFields={icdFields}
      modifierFields={modifierFields}
      rubricWorkflow={diagnosisWorkflow}
      hasRubricVersion={hasRubricVersion}
      onAnswerChange={onAttributeChange}
      onDeleteAnswer={onDeleteAttribute}
      onClearDownstream={onClearDownstream}
      evaluateFn={evaluateFn}
      workItemId={workItemId}
      spans={allSpans}
      editable={editable}
    />
  ));

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      size={size}
      onClose={onClose}
      blockScrollOnMount={false}
    >
      <DrawerOverlay />
      <DrawerContent className={`highlight-drawer ${!editable ? 'highlight-drawer-uneditable' : ''}`}>
        <DrawerHeader px={6} py={4}>
          <Text
            fontSize={pxToRem(18)}
            fontWeight="semibold"
            color={colorTheme.text.highEmphasis}
          >
            Highlight
          </Text>
          <DrawerCloseButton />
        </DrawerHeader>

        <DrawerBody px={6} py={4}>
          <VStack align="stretch">

            <Box className="code-chip-container">
             <Alert status="info" variant="left-accent" flexDirection="column" alignItems="flex-start">
     <AlertTitle fontSize={pxToRem(14)}>
                  Codes that still need supporting documentation
                </AlertTitle>
                <AlertDescription fontSize={pxToRem(12)}>
                  { editable ? 
                  filteredAvailableCodes?.length > 0 ? (
                    'Click one to apply to this text selection.'
                  ) : (
                    'There are no codes available to be attached.'
                  ) : 'View only mode - editing disabled.'}
                </AlertDescription>
              </Alert>
              {/* Pending Codes */}
              {filteredAvailableCodes?.length > 0 && (
                <Wrap spacing={3} mt={3}>
                  {filteredAvailableCodes.map((code) => (
                    <WrapItem
                      key={code.compositeId}
                      onClick={editable ? () => handleAttachCode(code) : undefined}
                      cursor={editable ? "pointer" : "default"}
                      _hover={editable ? { transform: 'scale(1.05)', boxShadow: 'md' } : {}}
                    >
                      <MedicalCodeChip
                        code={code.value}
                        codeType={code.codeType as CMxCommonApp.ChargeSessionCodeType}
                        description={code.description}
                        editable={false}
                      />
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </Box>


            <Alert status="info" className="selected-span-text">
              <HStack width="100%" justify="space-between" align="center">
                <Text flex={1}>"{selectedSpan?.coveredText}"</Text>
                {onDelete && editable && (
                  <IconButton
                    aria-label="Delete span"
                    icon={<Icon svgIconName="BiomeDelete" svgIconSize="16" />}
                    size="sm"
                    variant="ghost"
                    onClick={handleDeleteSpan}
                  />
                )}
              </HStack>
            </Alert>

            {/* Span Questions Section */}
            {spanWorkflow && (spanError ? (
              <Box>
                <Text fontSize={pxToRem(16)} fontWeight="semibold" color={colorTheme.text.highEmphasis} mb={2}>
                  Highlight Questions
                </Text>
                <Alert status="error" variant="left-accent" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Unable to load highlight questions. Please try refreshing the page.
                  </AlertDescription>
                </Alert>
                <Divider my={4} />
              </Box>
            ) : isSpanLoading && !spanEvaluation ? (
              <Box>
                <Text fontSize={pxToRem(16)} fontWeight="semibold" color={colorTheme.text.highEmphasis} mb={2}>
                  Highlight Questions
                </Text>
                <Center py={4}>
                  <Spinner size="md" />
                </Center>
                <Divider my={4} />
              </Box>
            ) : visibleSpanQuestions.length > 0 ? (
              <Box>
                <Text fontSize={pxToRem(16)} fontWeight="semibold" color={colorTheme.text.highEmphasis} mb={2}>
                  Highlight Questions
                </Text>
                {spanQuestionsDisabled && (
                  <Alert status="info" variant="left-accent" borderRadius="md" mb={2} sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'center !important' }}>
                    <AlertIcon mr={2} flexShrink={0} />
                    <AlertDescription fontSize="sm">
                      Please complete all case-level questions before answering highlight questions.
                    </AlertDescription>
                  </Alert>
                )}
                <VStack spacing={3} align="stretch">
                  {visibleSpanQuestions.map((wq) => (
                    <RubricQuestionField
                      key={wq.question.id}
                      workflowQuestion={wq}
                      currentAnswer={findAnswerForQuestion(
                        legacySpanAttributes,
                        wq.question.id
                      )}
                      onAnswerChange={(answerValueId) =>
                        handleSpanAttributeChange(wq.question.id, answerValueId)
                      }
                      isDisabled={spanQuestionsDisabled || !editable || isSpanLoading}
                      isAutofilled={spanAutofilledAnswersMap.has(wq.question.id)}
                      filteredAnswerIds={spanFilteredAnswersMap.get(wq.question.id)}
                      spans={allSpans}
                      isEvaluating={isSpanLoading && lastChangedSpanQuestionId === wq.question.id}
                    />
                  ))}
                </VStack>
                <Divider my={4} />
              </Box>
            ) : null)}

            {spanScopedWorkflowMatches.length > 0 && selectedSpan && spanScopedWorkflowMatches.map((m) => (
              <SpanScopedWorkflowSection
                key={`scope-${selectedSpan.id}-${m.sourceAttribute.id}`}
                match={m}
                span={selectedSpan}
                allSpans={allSpans}
                evaluateFn={evaluateFn}
                workItemId={workItemId}
                rubricVersionId={rubricVersionId}
                onAttributeChange={onAttributeChange}
                onDeleteAttribute={onDeleteAttribute}
                onClearDownstream={onClearDownstream}
                editable={editable}
              />
            ))}

            {showProcedure && (
              <>
                <Text
                  fontSize={pxToRem(16)}
                  fontWeight="semibold"
                  color={colorTheme.text.highEmphasis}
                >
                  Procedures
                </Text>
                {procedureCodeContainers}
                {codeSearch && procedureAllowNewCodes && (
                  <SuggestedLookup
                    ref={lookupRef}
                    actionPrefix=""
                    placeholderText={'Select procedure'}
                    debounceTimeout={30}
                    FieldDefinitions={FieldDefinitions.filter(
                      (def) => def.label.toLowerCase() === 'procedure'
                    )}
                    shouldShowResults={shouldShowResults}
                    autoFocus={false}
                    onSearch={onSearch}
                    onSearchSelection={handleCodeSelection}
                    searchResults={procedureSearchResults}
                    SearchBarViewVersion={'SearchBarView2'}
                    suggestedCodes={suggestedCodes}
                    allowCodeRemove={false}
                    transactionGuid={`${selectedSpan?.id}`}
                    existingCodes={procedureExistingCodes}
                    onRemoveAllCodes={createRemoveCodesHandler('procedure')}
                    disabled={!editable}
                  />
                )}
              </>
            )}

            {showModifier && (
              <>
                <Text
                  fontSize={pxToRem(16)}
                  fontWeight="semibold"
                  color={colorTheme.text.highEmphasis}
                >
                  Modifiers
                </Text>
                {modifierCodeContainers}
                {codeSearch && modifierAllowNewCodes && (
                  <SuggestedLookup
                    ref={modifierLookupRef}
                    actionPrefix=""
                    placeholderText={'Select modifier'}
                    debounceTimeout={30}
                    FieldDefinitions={[{ label: 'Modifier', value: 'modifier', key: 'MODIFIER' }]}
                    shouldShowResults={shouldShowResults}
                    autoFocus={false}
                    onSearch={onSearch}
                    onSearchSelection={handleCodeSelection}
                    searchResults={modifierSearchResults}
                    SearchBarViewVersion={'SearchBarView2'}
                    suggestedCodes={suggestedCodes}
                    transactionGuid={`${selectedSpan?.id}`}
                    existingCodes={modifierExistingCodes}
                    onRemoveAllCodes={createRemoveCodesHandler('modifier')}
                    disabled={!editable}
                  />
                )}
              </>
            )}

            {showDiagnosis && (
              <>
                <Text
                  fontSize={pxToRem(16)}
                  fontWeight="semibold"
                  color={colorTheme.text.highEmphasis}
                >
                  Diagnoses
                </Text>
                {diagnosisCodeContainers}
                {codeSearch && diagnosisAllowNewCodes && (
                  <SuggestedLookup
                    ref={diagnosisLookupRef}
                    actionPrefix=""
                    placeholderText={'Select diagnosis'}
                    debounceTimeout={30}
                    FieldDefinitions={[{ label: 'Diagnosis', value: 'icd', key: 'ICD' }]}
                    shouldShowResults={shouldShowResults}
                    autoFocus={false}
                    onSearch={onSearch}
                    onSearchSelection={handleCodeSelection}
                    searchResults={diagnosisSearchResults}
                    SearchBarViewVersion={'SearchBarView2'}
                    suggestedCodes={suggestedCodes}
                    allowCodeRemove={false}
                    transactionGuid={`${selectedSpan?.id}`}
                    existingCodes={diagnosisExistingCodes}
                    onRemoveAllCodes={createRemoveCodesHandler('diagnosis')}
                    disabled={!editable}
                  />
                )}
              </>
            )}


          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

// Export as both named and default for flexibility
export default HighlightDrawer;
